/**
 * Map a notification's (type, reference_type, reference_id) to an in-app path.
 *
 * The backend stores *what* a notification refers to — never the URL — so the
 * SPA can evolve routes without orphaning historical notifications. This
 * mapper is the single place that decides where each notification type lands
 * on click.
 *
 * Returns null for notifications with no useful destination (pure informational
 * alerts like "welcome"). Callers should skip navigation in that case.
 */
export function buildNotificationLink(
  type: string | null,
  referenceType: string | null,
  referenceId: string | null,
): string | null {
  const ref = referenceId || '';

  switch (type) {
    // HR / Leave
    case 'leave_approved':
    case 'leave_rejected':
    case 'leave_request':
    case 'leave_pending_approval':
      return ref ? `/hr/leave?leave_id=${ref}` : '/hr/leave';

    // Payroll
    case 'payroll_processed':
    case 'payroll_reminder':
    case 'payroll_approval_overdue':
    case 'payroll_approval_escalate':
    case 'payroll_autopilot_paused':
    case 'payroll_sync':
    case 'employee_payroll_ready':
      return ref ? `/payroll/runs/${ref}` : '/payroll';

    // Staff loan requests — land on the Loan Requests review tab (approve/deny).
    case 'loan_request':
      return '/payroll#loan-requests';

    // Onboarding
    case 'employee_onboarding_started':
    case 'new_hire':
      return ref ? `/hr/employees/${ref}` : '/hr/employees';

    // Tenants / Rent
    case 'rent_reminder':
      return ref ? `/tenants/${ref}` : '/tenants';

    // Intercompany
    case 'intercompany_confirmation':
      return ref ? `/group/intercompany/${ref}` : '/group/intercompany';

    // Approvals (expense, budget, etc.)
    case 'expense_approval':
    case 'approval_decision':
      return ref ? `/approvals/${ref}` : '/approvals';

    // Recruitment
    case 'recruitment_update':
      return ref ? `/recruitment/candidates/${ref}` : '/recruitment';

    // Lifecycle events — go to profile / settings
    case 'salary_change':
    case 'work_anniversary':
    case 'contract_expiry':
    case 'probation_review':
    case 'document_expiry':
      return ref ? `/hr/employees/${ref}` : '/profile';

    // Compliance
    case 'compliance_deadline':
    case 'tax_filing_reminder':
      return '/settings/compliance';

    // Benefits / self-service
    case 'nhf_opt_in_request':
      return '/profile?tab=benefits';

    // User-to-user
    case 'message':
      return '/messaging/inbox';

    // Announcements — no specific page; open full list
    case 'announcement':
    case 'system':
    case 'provider_test':
    case 'lifecycle':
      return '/notifications';

    // Onboarding nudge
    case 'welcome':
    case 'setup_reminder':
      return '/settings';

    default:
      break;
  }

  // Fallback: use reference_type if present and recognisable.
  if (referenceType && ref) {
    switch (referenceType) {
      case 'LeaveRequest':
      case 'leave_request':
        return `/hr/leave?leave_id=${ref}`;
      case 'Employee':
      case 'employee':
        return `/hr/employees/${ref}`;
      case 'PayrollRun':
      case 'payroll_run':
        return `/payroll/runs/${ref}`;
      case 'Invoice':
      case 'customer_invoice':
        return `/accounting/invoices/${ref}`;
      default:
        break;
    }
  }

  return null;
}
